'use client'
import { motion, useInView } from "motion/react";
import { TIndustriesConfig } from "@/app/config";
import { Globe, Zap, DollarSign } from "lucide-react";
import React, { useRef } from "react";

const metricsIcons = [
  {
    
    icon: Globe,
  },
  {
    
    icon: Zap,
  },
  {
    
    icon: DollarSign,
  },
];

export default function EntertainmentMarketOverview({config}:{config:TIndustriesConfig['entertainmentMarketOverview']}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  // Counter animation for numbers
  const useCountAnimation = (end:string, duration = 2) => {
    const [count, setCount] = React.useState(0);
    
    React.useEffect(() => {
      if (!isInView) return;
      
      let startTime: number | null = null;
      const startValue = 0;
      const endValue = parseInt(end.replace(/[^0-9]/g, ''));
      
      const animate = (timestamp : number) => {
        if (!startTime) startTime = timestamp;
        const progress = (timestamp - startTime) / (duration * 1000);
        
        if (progress < 1) {
          const current = Math.floor(startValue + (endValue - startValue) * progress);
          setCount(current);
          requestAnimationFrame(animate);
        } else {
          setCount(endValue);
        }
      };
      
      requestAnimationFrame(animate);
    }, [end, duration]); // ✅ Removed isInView from dependencies
    
    return count;
  };

  // ✅ Move hook calls outside of map - call hooks for each metric at component level
  // const animatedCounts = config.metrics.map((metric, index) => 
  //   // eslint-disable-next-line react-hooks/rules-of-hooks
  //   useCountAnimation(metric.value, 2 + index * 0.3)
  // );

  return (
    <motion.section 
      ref={ref}
      className="py-16 lg:py-20 bg-white relative overflow-hidden"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={containerVariants}
    >
      {/* Background decoration */}
      <motion.div
        className="absolute top-0 left-0 w-full h-full opacity-5"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.05 }}
        transition={{ duration: 2, ease: "easeOut" }}
      >
        <div className="absolute top-10 right-10 w-32 h-32 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-green-500 rounded-full blur-3xl" />
      </motion.div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-12"
          variants={ {
            hidden: { 
              opacity: 0, 
              y: -30,
              scale: 0.95
            },
            visible: { 
              opacity: 1, 
              y: 0,
              scale: 1,
              transition: {
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94]
              }
            }
          }}
        >
          <motion.h2 
            className="text-3xl lg:text-4xl font-normal text-gray-900 mb-4"
            initial={{ opacity: 0, y: -20, rotateX: -15 }}
            animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {config.title}
          </motion.h2>
          <motion.p 
            className="text-lg text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          >
            {config.description}
          </motion.p>
        </motion.div>

        {/* Metrics Cards */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
          variants={containerVariants}
          style={{ perspective: "1000px" }}
        >
          {config.metrics.map((metric, index) => {
            const Icons = metricsIcons[index].icon;
            // ✅ Use the pre-calculated animated count instead of calling the hook here
            
            
            return(
              <motion.div
                key={index}
                className={`${metric.bgColor} rounded-2xl p-8 lg:p-10 text-center border border-gray-100 relative overflow-hidden`}
                variants={ {
                  hidden: { 
                    opacity: 0, 
                    y: 50,
                    scale: 0.8,
                    rotateY: -20
                  },
                  visible: (index: number) => ({
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    rotateY: 0,
                    transition: {
                      duration: 0.7,
                      delay: index * 0.15,
                      ease: "backOut",
                      type: "spring",
                      damping: 20
                    }
                  }),
                  hover: {
                    scale: 1.08,
                    y: -10,
                    rotateY: 5,
                    rotateX: 5,
                    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
                    transition: {
                      duration: 0.3,
                      ease: "easeOut"
                    }
                  }
                }}
                custom={index}
                whileHover="hover"
                whileTap={{ scale: 0.95 }}
              >
                {/* Hover glow effect */}
                <motion.div
                  className="absolute inset-0 opacity-0 bg-gradient-to-r from-transparent via-white to-transparent"
                  whileHover={{
                    opacity: 0.1,
                    x: ["-100%", "100%"],
                    transition: { duration: 0.6, ease: "easeInOut" }
                  }}
                />

                {/* Icon */}
                <motion.div 
                  className="flex justify-center mb-6"
                  initial="initial"
                  animate="pulse"
                  whileHover="hover"
                >
                  <motion.div 
                    className={`w-16 h-16 ${metric.bgColor} rounded-full flex items-center justify-center shadow-lg`}
                    variants={{
                      initial: {
                        scale: 1,
                        rotate: 0,
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)"
                      },
                      hover: {
                        scale: 1.2,
                        rotate: 360,
                        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.2)",
                        transition: {
                          duration: 0.6,
                          ease: "easeInOut"
                        }
                      },
                      pulse: {
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          "0 4px 15px rgba(0, 0, 0, 0.1)",
                          "0 6px 20px rgba(0, 0, 0, 0.15)",
                          "0 4px 15px rgba(0, 0, 0, 0.1)"
                        ],
                        transition: {
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }
                      }
                    }}
                  >
                    <Icons className={`w-8 h-8 ${metric.textColor}`} />
                  </motion.div>
                </motion.div>

                {/* Metric Value */}
                <motion.div 
                  className={`text-4xl lg:text-6xl font-bold ${metric.textColor} mb-4`}
                  variants={{
                    hidden: { 
                      opacity: 0,
                      scale: 0.5,
                      rotateX: -90
                    },
                    visible: {
                      opacity: 1,
                      rotateX: 0,
                      transition: {
                        duration: 0.8,
                        delay: 0.3,
                        ease: "backOut"
                      }
                    },
                    hover: {
                      scale: 1.1,
                      textShadow: "0 0 20px rgba(0, 0, 0, 0.3)",
                      transition: {
                        duration: 0.2
                      }
                    }
                  }}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover="hover"
                  style={{ perspective: "200px" }}
                >
                  { metric.value}
                </motion.div>

                {/* Title */}
                <motion.h3 
                  className="text-xl lg:text-2xl font-semibold text-gray-900 mb-4"
                  variants={{
                    hidden: { 
                      opacity: 0,
                      x: -20
                    },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.5,
                        delay: 0.4,
                        ease: "easeOut"
                      }
                    }
                  }}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                >
                  {metric.title}
                </motion.h3>

                {/* Description */}
                <motion.p 
                  className="text-gray-600 text-sm lg:text-base leading-relaxed"
                  variants={ {
                    hidden: { 
                      opacity: 0,
                      x: 20
                    },
                    visible: {
                      opacity: 1,
                      x: 0,
                      transition: {
                        duration: 0.5,
                        delay: 0.6,
                        ease: "easeOut"
                      }
                    }
                  }}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                >
                  {metric.description}
                </motion.p>

                {/* Floating particles for visual interest */}
                {Array.from({ length: 3 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-2 h-2 ${metric.textColor.includes('blue') ? 'bg-blue-400' : 
                              metric.textColor.includes('green') ? 'bg-green-400' : 
                              'bg-yellow-400'} rounded-full opacity-30`}
                    style={{
                      left: `${20 + i * 25}%`,
                      top: `${10 + i * 15}%`,
                    }}
                    animate={{
                      y: [0, -20, 0],
                      opacity: [0.3, 0.7, 0.3],
                      scale: [1, 1.2, 1]
                    }}
                    transition={{
                      duration: 2 + i,
                      repeat: Infinity,
                      delay: i * 0.5,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </motion.div>
            )
          })}
        </motion.div>

        {/* Additional floating elements */}
        {Array.from({ length: 6 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-4 h-4 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-20"
            style={{
              left: `${10 + (i * 15)}%`,
              top: `${20 + (i * 10)}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 15, 0],
              rotate: [0, 180, 360],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
    </motion.section>
  );
}